"use client";
import React, { useEffect, useRef, useMemo, memo } from "react";
import { cn, formatNumberWithZeroCount } from "utils";
import CardLoading from "../Chat/Cards/CardLoading";
import Empty from "../Chat/Charts/Empty";
import { ChartDataType } from "../Chat/Cards/EcosystemDetail";
import { ChatCategoryCardType } from "../Chat/Cards/type";
import { createDualYAxisLineChart } from "@components/createChart";
import { useIsPc, useLineChart, useTokenDetailsMarket } from "@/src/hooks";
import calculateTimeRange from "@/src/utils/calculateTimeRange";
import { buildSubscriptNumberHtml } from "@/src/utils";

interface ChartContainerProps {
  className?: string;
  selectedDataType?: ChartDataType;
  dataType?: string;
  handleShowChart?: (type: any) => void;
  createdAt?: string;
  selectedTokenValues: string[];
  setSelectedDataType?: (type: string) => void;
  setShowComparisonToken?: (show: boolean) => void;
  symbol?: string;
  id?: string;
  isPollToken?: boolean;
}

const TokenDetailLine: React.FC<ChartContainerProps> = ({
  className = "",
  selectedDataType,
  dataType,
  selectedTokenValues,
  setShowComparisonToken,
  symbol,
  id,
  isPollToken,
}) => {
  const lineChartRef = useRef<HTMLDivElement>(null);
  const [isPC] = useIsPc();

  // const symbols = useMemo(() => {
  //   const currentSymbols = [symbol];
  //   if (!selectedTokenValues?.includes(symbol || "")) {
  //     currentSymbols.push(...selectedTokenValues);
  //   }
  //   return currentSymbols.map((s) =>
  //     typeof s === "string" ? s.toUpperCase() : s,
  //   );
  // }, [selectedTokenValues, symbol]);

  const { startTime, endTime } = useMemo(
    () => calculateTimeRange(dataType),
    [dataType],
  );
  const interval = dataType === "24h" ? "5m" : "1h";

  const queryParams = {
    "enrich.enable": true,
    "enrich.with_ohlc.enable": true,
    "enrich.with_ohlc.start_time": startTime,
    "enrich.with_ohlc.end_time": endTime,
    "enrich.with_ohlc.interval": interval,
    // 媒体信息
    // "enrich.with_token.with_social_metrics": true,
  };

  const { data: dataTokenList, isLoading: freshDataLoading } =
    useTokenDetailsMarket({
      enabled: true,
      dataType: dataType,
      // symbols: symbols as string[],
      ids: [id as string],
      queryParams,
      isPollToken,
    });

  const currentSymbolData = dataTokenList?.find(
    (item: any) => item?.[isPollToken ? "base_symbol" : "symbol"] === symbol,
  );

  const sparkline = {
    [symbol || ""]: currentSymbolData?.ohlc?.map((item: any) => ({
      price: item.close,
      timestamp: item.time,
    })),
  };

  // useEffect(() => {
  //   // 如果数据中30D的数据为空，也就是7D的数据也是空的则默认进行查询24h的数据
  //   if (
  //     sparkline &&
  //     dataType !== DataType["1D"] &&
  //     (sparkline[symbol || ""]?.length ?? 0) <= 0 &&
  //     !freshDataLoading &&
  //     !initSparkline.current
  //   ) {
  //     // 触发请求24h的数据
  //     setSelectedDataType?.(DataType["1D"]);
  //     initSparkline.current = true;
  //   }
  // }, [dataType, freshDataLoading, setSelectedDataType, sparkline, symbol]);

  useEffect(() => {
    // 有值存在的话，才进行显示eth和sol的对比按钮
    const showComparisonToken = (sparkline[symbol || ""]?.length ?? 0) > 0;
    setShowComparisonToken?.(showComparisonToken);
  }, [
    dataType,
    selectedTokenValues,
    setShowComparisonToken,
    sparkline,
    symbol,
  ]);

  // 这里控制对比的图标数据，当选中ETH和SOL的时候，对比图数据需要添加这两条线的数据
  // 选中的对比数据是selectedTokenValues
  const comparisonToken = () => {
    const comparisonTokenData: Record<string, any> = {};
    return comparisonTokenData;
  };

  // tokenKlineData = { BTC: [], ETH: []}
  const tokenKlineData = {
    [symbol || ""]: sparkline[symbol || ""],
    ...comparisonToken(),
  };

  const cardItem = {
    type: "token",
    properties: { token_symbol: symbol, icon: currentSymbolData?.logo },
  };

  const { xAxisData, yAxisData, xKeyTicks } = useLineChart(
    cardItem as ChatCategoryCardType,
    selectedDataType,
    () => {}, // TODO. 暂时隐藏数据处理
    tokenKlineData,
  );

  useEffect(() => {
    const clear = createDualYAxisLineChart({
      config: {
        el: lineChartRef.current!,
        tooltipValueFormat: (value: MaybeOrNull<number>) => {
          if (value === null) {
            return "";
          }
          if (value < 1 && value > 0) {
            const { integerPart, zeroCount, leftDigits } =
              formatNumberWithZeroCount({
                numStr: value.toFixed(20),
                decimals: 2,
                minZeroCount: 2,
              });
            const html = buildSubscriptNumberHtml({
              integerPart,
              zeroCount,
              restOfDecimal: leftDigits,
              spanClass: "text-[0.8em]",
            });
            return `$${html}`;
          }
          return `$${value.toLocaleString(undefined, {
            maximumFractionDigits: 10,
          })}`;
        },
        tooltipFormatter: isPC ? undefined : () => " ",
      },
      XAxis: xAxisData as any,
      YAxis: yAxisData as any,
      ticks: {
        xKeyTicks: xKeyTicks as any,
      } as any,
    });
    return () => {
      clear?.cleanup();
    };
  }, [xAxisData, yAxisData, xKeyTicks]);

  const containerClass =
    "w-full max-w-212.5 h-45.5 min-h-45.5 [content-visibility:auto]";

  // left必须有值，右侧对比值根据check按钮进行选中和取消选中
  if ((!freshDataLoading && !xAxisData?.length) || !yAxisData?.left?.length) {
    return (
      <Empty
        className={containerClass}
        title=""
        description="No Data Yet"
        descriptionClassName="body_medium_m"
        src="/assets/images/chart_no_data.webp"
        hiddenTitle
        imageSize={72}
      />
    );
  }

  if (freshDataLoading) {
    return <CardLoading className={cn(containerClass)} />;
  }
  return (
    <div
      className={cn(containerClass, className)}
      ref={lineChartRef}
      data-horizontal-scroll="true"
    ></div>
  );
};

export default memo(TokenDetailLine);
